import { useEffect, useRef, useState } from 'react';
import { Laptop, Package, Search } from 'lucide-react';
import type { AssetDetail, CustodyNotification } from '@skhub/contracts';
import { useToast } from '@/app/toast';
import { ApiError } from '@/lib/api';
import { useAsset, useAssetAction, useAssets, useEmployees, useMeta } from '@/lib/queries';
import { Avatar, CategoryIcon, Field, FormError, Modal } from '@/components/ui';
import { useDebounced } from './AssetsPage';
import { NotifyToggle, emailNote } from './AssetActions';

export type AssignPerson = { id: string; displayName: string; employeeCode: string; email: string; team: string | null };

/**
 * Assign an available device starting from the person instead of the asset. With `employee` the person is
 * fixed (employee page); without it the dialog asks for both (quick actions). Same API call, same audit
 * trail and confirmation email as assigning from the asset page.
 */
export function AssignAssetDialog({ employee, onClose }: { employee?: AssignPerson; onClose: () => void }) {
  const meta = useMeta();
  const toast = useToast();
  const [person, setPerson] = useState<AssignPerson | null>(employee ?? null);
  const [assetId, setAssetId] = useState('');
  const asset = useAsset(assetId || undefined);
  const action = useAssetAction<Record<string, unknown>>(assetId, 'assign');
  // Version of the picked asset as first seen, so a background refresh never hides someone else's change.
  const opened = useRef<{ id: string; rowVersion: string } | null>(null);
  useEffect(() => {
    if (asset.data && asset.data.id === assetId && opened.current?.id !== assetId) opened.current = { id: assetId, rowVersion: asset.data.rowVersion };
  }, [asset.data, assetId]);

  const [handover, setHandover] = useState('');
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [local, setLocal] = useState<{ person?: string; asset?: string }>({});
  const [error, setError] = useState<unknown>(null);
  const fields = (error instanceof ApiError && error.fields) || {};

  const submit = async () => {
    const missing = { person: person ? undefined : 'Choose who receives the device', asset: assetId ? undefined : 'Choose an available device' };
    setLocal(missing);
    if (!person || !assetId) return;
    if (opened.current?.id !== assetId) return setLocal({ asset: 'Still loading the device. Try again in a moment.' });
    setError(null);
    try {
      const res = (await action.mutateAsync({
        rowVersion: opened.current.rowVersion,
        employeeId: person.id,
        handoverCondition: handover || null,
        ticketReference: ticket,
        reason,
        note,
        notify,
      })) as AssetDetail & { notification?: CustodyNotification };
      const failed = res.notification?.status === 'failed';
      toast({ kind: failed ? 'error' : 'success', title: 'Asset assigned', detail: `${res.assetTag} to ${person.displayName}${emailNote(res.notification)}` });
      onClose();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      width="max-w-xl"
      title="Assign an asset"
      description={employee ? `Give ${employee.displayName} an available device` : 'Choose a person and an available device'}
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-5"
      >
        <FormError error={error} fieldsInline />
        {employee ? (
          <div className="flex items-center gap-3 rounded-[var(--radius-md)] bg-surface-2 px-3 py-2.5">
            <Avatar name={employee.displayName} size={30} />
            <span className="min-w-0 text-[13px]">
              <span className="block truncate font-medium text-fg">{employee.displayName}</span>
              <span className="block truncate text-xs text-fg-3">
                <span className="mono">{employee.employeeCode}</span>
                {employee.team ? ` · ${employee.team}` : ''}
              </span>
            </span>
          </div>
        ) : (
          <PersonPicker value={person} onChange={setPerson} error={local.person || fields.employeeId} />
        )}

        <AssetPicker value={assetId} onChange={setAssetId} availableStatus={meta.data?.availableStatus} error={local.asset} autoFocus={!!employee} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Handover condition" htmlFor="qa-cond" error={fields.handoverCondition}>
            <select id="qa-cond" className="field" value={handover} onChange={(e) => setHandover(e.target.value)}>
              <option value="">Not recorded</option>
              {(meta.data?.handoverConditions ?? []).map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Ticket reference" htmlFor="qa-ticket" error={fields.ticketReference}>
            <input id="qa-ticket" className="field mono" value={ticket} onChange={(e) => setTicket(e.target.value)} maxLength={160} />
          </Field>
        </div>
        <Field label="Reason" htmlFor="qa-reason" error={fields.reason}>
          <input id="qa-reason" className="field" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={160} placeholder="e.g. New joiner, replacement" />
        </Field>
        <Field label="Note" htmlFor="qa-note" error={fields.note}>
          <textarea id="qa-note" className="field" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </Field>
        <NotifyToggle checked={notify} onChange={setNotify} who={person?.displayName} email={person?.email} kind="assigned" />

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.isPending}>
            {action.isPending ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PersonPicker({ value, onChange, error }: { value: AssignPerson | null; onChange: (p: AssignPerson) => void; error?: string }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 200);
  const people = useEmployees({ q: dq || undefined, pageSize: 6, sort: 'displayName' });
  const rows = people.data?.items.filter((e) => e.isActive);
  return (
    <fieldset>
      <legend className="label">
        Employee <span className="text-danger">*</span>
      </legend>
      <SearchBox value={q} onChange={setQ} label="Search employees" placeholder="Search name, email or employee ID" autoFocus />
      <div role="radiogroup" aria-label="Matching employees" className="mt-2 max-h-48 overflow-y-auto rounded-[var(--radius-md)] border border-line">
        {people.isError ? (
          <p className="px-3 py-3 text-xs text-danger">Could not load employees.</p>
        ) : !rows ? (
          <p className="px-3 py-3 text-xs text-fg-3">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="px-3 py-3 text-xs text-fg-3">No active employee matches.</p>
        ) : (
          rows.map((e) => {
            const checked = value?.id === e.id;
            return (
              <label key={e.id} className={`flex cursor-pointer items-center gap-3 border-b border-line px-3 py-2 last:border-0 ${checked ? 'bg-brand-soft' : 'hover:bg-surface-2'}`}>
                <input type="radio" name="qa-employee" checked={checked} onChange={() => onChange(e)} className="accent-[var(--brand)]" />
                <Avatar name={e.displayName} size={26} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-fg">{e.displayName}</span>
                  <span className="block truncate text-xs text-fg-3">
                    <span className="mono">{e.employeeCode}</span>
                    {e.team ? ` · ${e.team}` : ''}
                    {e.laptopCount ? ` · holds ${e.laptopCount} laptop${e.laptopCount > 1 ? 's' : ''}` : ''}
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </fieldset>
  );
}

function AssetPicker({ value, onChange, availableStatus, error, autoFocus }: { value: string; onChange: (id: string) => void; availableStatus?: string; error?: string; autoFocus?: boolean }) {
  const [q, setQ] = useState('');
  const [laptops, setLaptops] = useState(true);
  const dq = useDebounced(q, 200);
  const list = useAssets({ status: availableStatus, assigned: 'no', laptop: laptops ? '1' : undefined, q: dq || undefined, pageSize: 8, sort: 'assetTag', dir: 'asc' });
  const total = list.data?.total;
  return (
    <fieldset disabled={!availableStatus}>
      <legend className="label">
        Device <span className="text-danger">*</span>
      </legend>
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <SearchBox value={q} onChange={setQ} label="Search available devices" placeholder="Search tag, serial, model or name" autoFocus={autoFocus} />
        </div>
        <div role="radiogroup" aria-label="Device type" className="inline-flex shrink-0 rounded-[var(--radius-md)] border border-line bg-surface-2 p-0.5">
          {(
            [
              [true, 'Laptops', Laptop],
              [false, 'All', Package],
            ] as const
          ).map(([v, label, Icon]) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={laptops === v}
              onClick={() => setLaptops(v)}
              className={`inline-flex h-8 items-center gap-1.5 rounded-[6px] px-2.5 text-xs font-medium transition-colors ${laptops === v ? 'bg-surface text-fg shadow-[var(--shadow-rest)]' : 'text-fg-2 hover:text-fg'}`}
            >
              <Icon aria-hidden className="size-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>
      <div role="radiogroup" aria-label="Available devices" className="mt-2 max-h-56 overflow-y-auto rounded-[var(--radius-md)] border border-line">
        {list.isError ? (
          <p className="px-3 py-3 text-xs text-danger">Could not load devices.</p>
        ) : !list.data ? (
          <p className="px-3 py-3 text-xs text-fg-3">Loading…</p>
        ) : list.data.items.length === 0 ? (
          <p className="px-3 py-3 text-xs text-fg-3">{dq ? 'No available device matches.' : `No ${laptops ? 'laptops' : 'devices'} are available right now.`}</p>
        ) : (
          list.data.items.map((a) => {
            const checked = value === a.id;
            return (
              <label key={a.id} className={`flex cursor-pointer items-center gap-3 border-b border-line px-3 py-2 last:border-0 ${checked ? 'bg-brand-soft' : 'hover:bg-surface-2'}`}>
                <input type="radio" name="qa-asset" checked={checked} onChange={() => onChange(a.id)} className="accent-[var(--brand)]" />
                <span className={`grid size-7 shrink-0 place-items-center rounded-[6px] ${a.isLaptop ? 'bg-brand-soft text-brand-ink' : 'bg-surface-2 text-fg-3'}`}>
                  <CategoryIcon type={a.assetType} className="size-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="mono block truncate text-[13px] font-medium text-fg">{a.assetTag}</span>
                  <span className="block truncate text-xs text-fg-3">
                    {a.assetType} · {a.name}
                    {a.serialNumber ? <span className="mono"> · {a.serialNumber}</span> : null}
                  </span>
                </span>
                {a.location && <span className="hidden shrink-0 text-2xs text-fg-3 sm:block">{a.location}</span>}
              </label>
            );
          })
        )}
      </div>
      <p className="mt-1 text-xs text-fg-3">
        {total !== undefined && total > (list.data?.items.length ?? 0) ? `Showing ${list.data?.items.length} of ${total} available. Search to narrow down.` : 'Only devices in stock and not held by anyone are listed.'}
      </p>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </fieldset>
  );
}

function SearchBox({ value, onChange, label, placeholder, autoFocus }: { value: string; onChange: (v: string) => void; label: string; placeholder: string; autoFocus?: boolean }) {
  return (
    <div className="relative">
      <Search aria-hidden className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-3" />
      <input aria-label={label} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="field pl-9" autoFocus={autoFocus} />
    </div>
  );
}
